import React, { useRef } from 'react';
import PropTypes from 'prop-types';
//widgets
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import PlainButton from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
//local
import ClickCopy from '../ClickCopy/ClickCopy';
import { makeStyles } from '@material-ui/core';

function TypographyInput(props) {
  const {
    label,
    color = undefined,
    text,
    variant,
    context,
    copy,
    copyValue,
    click,
    clickTooltip,
    highlightText,
  } = props;
  const isButton = copy || typeof click === 'function';
  const labelRef = useRef(null);
  const classes = useStyles(
    color,
    labelRef.current?.offsetWidth ?? `${label.length}ch`
  )();

  const makeHighlight = (text) => {
    const length = text.length;
    const sliceLength = length - highlightText;
    return (
      <span className={classes.highlight}>
        {text.slice(0, sliceLength)}
        <span className={classes.highlight}>
          {text.slice(sliceLength, length)}
        </span>
      </span>
    );
  };

  return (
    <FormControl>
      <InputLabel
        ref={labelRef}
        shrink={true}
        className={isButton ? classes.buttonLabel : classes.label}
      >
        {label}
      </InputLabel>
      <div className={isButton ? classes.button : classes.text}>
        {copy ? (
          <ClickCopy
            text={text}
            value={copyValue ? copyValue : text}
            variant={variant}
            context={context}
            highlightText={highlightText}
          />
        ) : isButton ? (
          <Tooltip title={clickTooltip}>
            <PlainButton onClick={click}>
              <Typography
                variant={variant}
                className={context ? classes[context] : undefined}
              >
                {highlightText ? highlight(text) : text}
              </Typography>
            </PlainButton>
          </Tooltip>
        ) : (
          <Typography
            variant={variant}
            className={context ? classes[context] : undefined}
          >
            {highlightText ? highlight(text) : text}
          </Typography>
        )}
      </div>
    </FormControl>
  );
}

TypographyInput.defaultProps = {
  variant: 'subtitle1',
  copy: false,
  clickTooltip: '',
};

TypographyInput.propTypes = {
  color: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.oneOf([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'caption',
    'button',
    'overline',
    'srOnly',
    'inherit',
    'display4',
    'display3',
    'display2',
    'display1',
    'headline',
    'title',
    'subheading',
  ]),
  context: PropTypes.oneOf(['success', 'error', 'warn']),
  copy: PropTypes.bool,
  copyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  click: PropTypes.func,
  clickTooltip: PropTypes.string,
  highlightText: PropTypes.number,
};

const useStyles = (color, labelWidth) =>
  makeStyles((theme) => ({
    text: {
      color: color,
      marginTop: theme.spacing(2.5),
      minWidth: labelWidth,
    },
    button: {
      color: color,
      marginTop: theme.spacing(2) - 2,
      textTransform: 'capitalize',
    },
    highlight: {
      '&:hover': {
        '& > *': {
          color: theme.palette.primary.main,
        },
      },
    },
    label: {
      color: color,
      whiteSpace: 'nowrap',
    },
    buttonLabel: {
      color: color,
      paddingLeft: 10,
      whiteSpace: 'nowrap',
    },
    success: {
      color: '#28a745',
    },
    error: {
      color: '#dc3545',
    },
    warn: {
      color: '#ffc107',
    },
  }));

export default TypographyInput;
